import React, { useEffect, useRef, useState } from "react";
import Loader from "../../Components/Common/Loader";

/**
 * Defers mounting children until the section is near the viewport,
 * or after an optional delay. Heavy KPI sections can load last
 * without blocking above-the-fold widgets.
 */
const LazySection = ({
  children,
  rootMargin = "200px 0px",
  delayMs = 0,
  minHeight = 180,
  className = "",
  enabled = true,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(delayMs <= 0);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setVisible(false);
      return undefined;
    }
    if (delayMs <= 0) {
      setReady(true);
      return undefined;
    }
    const timer = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, enabled]);

  useEffect(() => {
    if (!enabled || !ready || visible) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ready, visible, rootMargin, enabled]);

  return (
    <div ref={ref} className={className} style={{ minHeight: visible ? undefined : minHeight }}>
      {!enabled || !ready || !visible ? <Loader /> : children}
    </div>
  );
};

export default LazySection;
